import * as React from 'react';
import { formatPattern } from 'react-router';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import useRouter from 'js/lib/useRouter';

import type { SideNavItem, SideNavSubItem } from 'bundles/enterprise-admin-navigation/components/side-nav/base/types';
import {
  type SettingsSideNavItemConfig,
  type SettingsSideNavSubItemConfig,
  settingsSideNavConfigDefs,
} from 'bundles/enterprise-admin-navigation/components/side-nav/config/settingsSideNavConfigDefs';
import { computeSearchCarry } from 'bundles/enterprise-admin-navigation/utils/navConfigUtils';

const resolveSubItem = (
  child: SettingsSideNavSubItemConfig,
  router: InjectedRouter,
  locationSearchString: string
): SideNavSubItem => {
  // Format route template with org slug parameter
  const formattedHref = formatPattern(child.href, router.params);
  return {
    id: child.id,
    label: child.getLabel(),
    href: `${formattedHref}${locationSearchString}`,
    // `indexOnly` (2nd arg) forces an exact pathname match. Settings routes are
    // nested and some share prefixes (e.g. `/settings` is a prefix of every
    // item), so the default prefix match would light up multiple items at once.
    isActive: router.isActive(formattedHref, true),
  };
};

const resolveItem = (
  item: SettingsSideNavItemConfig,
  router: InjectedRouter,
  locationSearchString: string
): SideNavItem => {
  const children = item.children?.map((child) => resolveSubItem(child, router, locationSearchString));

  // Expandable parents have no href of their own — only leaf items navigate.
  const formattedHref = item.href ? formatPattern(item.href, router.params) : undefined;

  return {
    id: item.id,
    label: item.getLabel(),
    icon: item.icon,
    href: formattedHref ? `${formattedHref}${locationSearchString}` : undefined,
    // Exact match (see resolveSubItem) so prefix-sharing leaves don't all appear active.
    isActive: formattedHref ? router.isActive(formattedHref, true) : false,
    children,
  };
};

/**
 * Resolves the declarative settings side-nav config into ready-to-render items.
 *
 * Mirrors `useAdminSideNavItems` but for the settings section: walks
 * `settingsSideNavConfigDefs`, formatting hrefs with the org slug and computing
 * `isActive` against the current route. Returned items feed straight into the
 * `SideNav` `items` prop and are rendered by `SideNavList` (expandable parents
 * are detected via their `children`).
 */
export const useSettingsSideNavItems = (): SideNavItem[] => {
  const router = useRouter();
  const { pathname, search } = router.location;

  const locationSearchString = React.useMemo(() => computeSearchCarry(search || ''), [search]);

  // The client router mutates the same `router` object in place on navigation
  // (see `extendRenderProps` in `bundles/page/lib/router.tsx`), so its reference
  // is stable. Key off `pathname` (and the carried search) — the values that
  // actually drive href + `isActive` resolution — so the items recompute with
  // fresh active flags on every in-section navigation.
  return React.useMemo(
    () => settingsSideNavConfigDefs.map((item) => resolveItem(item, router, locationSearchString)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router, pathname, locationSearchString]
  );
};
